/**
 * @file OptionRow.tsx
 * @description MultipleCascader 面板中单列内单个选项的渲染与交互组件
 *
 * 模块职责：
 * - 渲染选项行：复选框 + label + 展开箭头（若有子级）
 * - 计算选中态：叶子节点直接判断，父节点基于叶子命中数计算全选/半选
 * - 处理点击：行点击/箭头点击展开子级，复选框点击仅影响 value
 *
 * 业务约束（来源：doc/requirements.md）：
 * - 禁用叶子不可交互；禁用项若有子级仍允许展开（保持可探索性）。
 */

import React from 'react';
import classNames from 'classnames';
import { Checkbox } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';

import type { ICascaderMenuOption, TStringPath } from '../types';
import { findPathOptions } from '../utils';
import {
  collectLeafPaths,
  getOptionLabelText,
  isPathChecked,
} from './panelUtils';

interface IOptionRowProps {
  /** 当前选项 */
  option: ICascaderMenuOption;
  /** 所在列层级（0-based） */
  level: number;
  /** 当前展开路径 */
  expandedPath: string[];
  /** 已选路径数组 */
  value: TStringPath[];
  /** 过滤后的数据树（用于 resolve 路径） */
  filteredData: ICascaderMenuOption[];
  /** 点击选项/箭头时展开子级 */
  onExpand: (option: ICascaderMenuOption, level: number) => void;
  /** 复选框勾选变化 */
  onCheckboxChange: (
    checked: boolean,
    option: ICascaderMenuOption,
    level: number,
    originContext: unknown,
  ) => void;
}

/**
 * 选项行组件：封装单行选项的选中态计算与渲染逻辑。
 *
 * 选中态规则：
 * - 叶子：直接判断 path 是否在 value 中
 * - 父节点：0 命中未选，全命中全选，介于两者为半选（indeterminate）
 */
const OptionRow: React.FC<IOptionRowProps> = ({
  option,
  level,
  expandedPath,
  value,
  filteredData,
  onExpand,
  onCheckboxChange,
}) => {
  const hasChildren =
    Array.isArray(option.children) && option.children.length > 0;
  const isExpanded = expandedPath[level] === option.value;
  const prefix = expandedPath.slice(0, level);
  const path: TStringPath = [...prefix, option.value];

  const optionChain = findPathOptions(filteredData, path) || [];
  const targetOption =
    optionChain.length > 0 ? optionChain[optionChain.length - 1] : option;
  const leafPaths =
    targetOption && optionChain.length > 0
      ? collectLeafPaths(targetOption, path)
      : [];

  let checked = false;
  let indeterminate = false;

  if (!leafPaths.length) {
    checked = isPathChecked(value, path);
  } else {
    const total = leafPaths.length;
    let selectedCount = 0;
    leafPaths.forEach((leafPath) => {
      if (isPathChecked(value, leafPath)) {
        selectedCount += 1;
      }
    });

    if (selectedCount === 0) {
      checked = false;
    } else if (selectedCount === total) {
      checked = true;
    } else {
      indeterminate = true;
    }
  }

  const labelText = getOptionLabelText(option.label);

  const handleRowClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    if (option.disabled && !hasChildren) {
      return;
    }
    onExpand(option, level);
  };

  const handleArrowClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    onExpand(option, level);
  };

  const handleCheckboxClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
  };

  const handleCheckboxChange = (
    nextChecked: boolean | string,
    originContext: unknown,
  ): void => {
    onCheckboxChange(Boolean(nextChecked), option, level, originContext);
  };

  return (
    <div
      key={option.value}
      className={classNames('dbl-multiple-cascader-option', {
        'dbl-multiple-cascader-option--has-children': hasChildren,
        'dbl-multiple-cascader-option--expanded': isExpanded,
        'dbl-multiple-cascader-option--disabled': option.disabled,
      })}
      onClick={handleRowClick}
    >
      <div
        className="dbl-multiple-cascader-option-checkbox"
        onClick={handleCheckboxClick}
      >
        <Checkbox
          disabled={option.disabled}
          value={checked}
          indeterminate={indeterminate}
          onChange={handleCheckboxChange}
        />
      </div>
      <span
        className="dbl-multiple-cascader-option-label"
        title={labelText}
      >
        {option.label}
      </span>
      {hasChildren ? (
        <span
          className="dbl-multiple-cascader-option-arrow"
          onClick={handleArrowClick}
        >
          <SvgIcon type="point-right" />
        </span>
      ) : null}
    </div>
  );
};

export default React.memo(OptionRow);
